/* CWR 2.4 — Service / Event detail page. CSS copied from the confirmed mockup
   (docs/mockups/client-dashboard-redesign/session-detail-layouts.html); the only
   deltas are token swaps + the fullscreen layout adaptation. HONEST swap log:
     · var(--font-display)            → var(--client-font-heading), the theme's heading font (matches ClientShell/catalog)
     · var(--r-sm|md|lg|xl|pill)      → literal px (radii are not injected tokens)
     · var(--capacity-fill)           → var(--capacity-fill-bg)  (the injected name)
     · ring gradients rgba(--accent-light-rgb,…)
          .sd-stats::before  (mock 0.18→0.03) → var(--catalog-card-ring-bg)  (0.22→0.04 — imperceptible, reuses 2.2's token)
          .sd-prov::before   (mock 0.16→0.03) → var(--catalog-tab-ring-bg)   (exact match)
     · var(--accent-hot) usages →
          event .spots                         → var(--spots-low-color)      (semantic match; 2.2 token)
          .date-chip.selected .dow, .time-pill.selected, .sd-prov .qk
                                               → var(--event-date-color)     (warm date/time emphasis; all are --accent-hot aliases)
   NO new tokens minted (every accent-hot/accent-light surface maps to an existing
   injected token). The mockup's Telegram chrome (.sd-close / .sd-kebab) is NOT
   ported — that is Telegram's own fullscreen chrome; we render only .back. */

/* Paused notice shares the provider card's glass surface and spacing below. */
.closed { grid-template-columns: 44px minmax(0, 1fr); }
.closedIcon { width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center; box-shadow: inset 0 0 0 1.5px rgba(var(--accent-rgb), 0.5); color: var(--accent); }
.closedIcon svg { width: 18px; height: 18px; stroke-width: 2.2; }
.closedTitle { font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif); font-size: 17px; font-weight: 600; color: var(--text-primary); letter-spacing: -0.005em; }
.closedSubtitle { font-size: 12.5px; color: var(--text-secondary); margin-top: 2px; }

/* ── Page + scroll (fullscreen adaptation of the mockup's .iphone-frame/.app) ── */
.page {
  position: fixed;
  inset: 0;
  background: var(--bg-page, #050403);
  color: var(--text-primary, #faf3e8);
  overflow: hidden;
}
.scroll {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  /* clearance for the fixed bottom action bar */
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 96px);
  /* Plain native momentum scroll. `overscroll-behavior: none` suppresses iOS's
     rubber-band BOUNCE at the top/bottom of this scroller (the deceleration
     curve itself is OS-owned and not tunable). No snap/detents — the sheet
     rests wherever the finger leaves it, like any normal mobile page. */
  overscroll-behavior: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}

/* ── Back button — pixel-identical to the catalog's .pageBackBtn, fixed at the
   same screen spot (clears the Telegram chrome). ── */
/* rd-bookings — positioning only; the glass square + arrow are owned by the
   shared FlowBackButton (design D6). */
.back {
  position: fixed;
  top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) +
      var(--tg-content-safe-area-inset-top, 0px) + 12px
  );
  left: 18px;
  z-index: 80;
}
/* On-hero page title ("Service Details" / "Event Details"), aligned to the back
   button band (NOT up in the Telegram chrome). */
/* The nav page-title is visually hidden — the hero name is the page heading.
   Kept in the DOM (sr-only) for screen-reader page context. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Hero (pinned BEHIND the scroller — static; the sheet scrolls over it) ── */
.hero {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  height: 330px;
  background-size: cover;
  background-position: center;
}
/* Transparent window in the scroller revealing the pinned hero underneath. */
.heroSpacer {
  height: 330px;
  flex-shrink: 0;
  pointer-events: none;
}
.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  /* The top band only; the fade under the words rides the caption (.heroCap::before),
     as the booking flow's hero does (Gabriel 2026-10-22). */
  background: linear-gradient(180deg, rgba(var(--bg-page-rgb), 0.35) 0%, rgba(var(--bg-page-rgb), 0) 30%);
}
.heroFallback {
  background-image: linear-gradient(135deg, var(--glass-bg-strong), var(--bg-page));
}
.heroCap {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  /* The subline clear of the sheet's edge, as the booking flow's caption (Gabriel 2026-10-22). */
  padding: 0 20px 16px;
}
/* The booking flow's fade (BookingHero .cap::before, Gabriel 2026-10-22): it ends near
   the top of the name, however many lines the name and summary take. */
.heroCap::before {
  content: '';
  position: absolute;
  z-index: -1;
  top: -14px;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(var(--bg-page-rgb), 0) 0%,
    rgba(var(--bg-page-rgb), 0.5) 28%,
    rgba(var(--bg-page-rgb), 0.82) 62%,
    var(--bg-page) 100%
  );
}
.title {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 35px;
  letter-spacing: -0.01em;
  line-height: 1;
  color: var(--text-primary);
  /* The booking flow's glow behind the name and its small line (BookingHero .name, .when). */
  text-shadow: 0 0 4px rgba(var(--bg-page-rgb), 0.6), 0 2px 18px var(--bg-page);
}
.subtitle {
  margin-top: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
  max-width: 300px;
  text-shadow: 0 0 3px rgba(var(--bg-page-rgb), 0.9), 0 1px 10px var(--bg-page), 0 0 22px rgba(var(--bg-page-rgb), 0.9);
}

/* ── Rising sheet (= the body) ── */
.sheet {
  position: relative;
  z-index: 1;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Soft lift only — the previous heavier upward shadow darkened the hero's
     short description (summary) sitting just above the sheet edge. */
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: -4px auto 0;
}
.body {
  padding: 16px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ── Stats card (service) ── */
.stats {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  padding: 10px 8px;
  position: relative;
}
.stats::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--catalog-card-ring-bg);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-align: center;
  padding: 0 4px;
}
.stat + .stat {
  border-left: 1px solid var(--border-subtle);
}
.statLbl {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.statVal {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}
.statVal.price {
  color: var(--accent);
}

/* ── Description ── */
.desc {
  font-size: 13.5px;
  color: var(--text-secondary);
  line-height: 1.55;
}

/* ── Gallery ── */
.gallery {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin: 0 -20px;
}
.galleryTrack {
  display: flex;
  gap: 9px;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0 20px;
}
.galleryTrack::-webkit-scrollbar {
  display: none;
}
.shot {
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  display: block;
  flex: 0 0 116px;
  height: 112px;
  border-radius: 14px;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px var(--border-subtle);
}
.shotImage {
  position: absolute;
  inset: 0;
}
.dots {
  display: flex;
  gap: 6px;
  justify-content: center;
}
.dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-muted);
}
.dots i.on {
  background: var(--accent);
  width: 16px;
  border-radius: 3px;
}

/* ── Provider — "Questions? Chat with" ──
   CWR closeout D6: this mockup-confirmed SessionDetail anatomy intentionally
   stays separate from the FocusedScreenShell ChatRow; do not deduplicate it as
   a third copy of the focused-screen row. ── */
.prov {
  grid-template-columns: 44px 1fr 40px;
}
.prov,
.closed {
  position: relative;
  display: grid;
  align-items: center;
  gap: 12px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  padding: 10px 13px;
}
.prov::before,
.closed::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--catalog-tab-ring-bg);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1.5px rgba(var(--accent-rgb), 0.5);
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--accent);
}
/* client-chat-screen D7: the business's initial on the accent gradient (ChatRow's gradientAvatar). */
.avatarBusiness {
  background-image: radial-gradient(circle at 30% 28%, var(--accent-hot) 0%, var(--accent-deep) 100%);
  color: var(--text-on-accent);
}
.who {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.qk {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--event-date-color);
}
.provName {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 19px;
  color: var(--text-primary);
  margin: 3px 0 1px;
  letter-spacing: -0.005em;
  line-height: 1.1;
}
.provLine {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chat {
  appearance: none;
  -webkit-appearance: none;
  padding: 0;
  background: transparent;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(var(--accent-rgb), 0.5);
  color: var(--accent);
  display: grid;
  place-items: center;
}
.chat svg {
  width: 18px;
  height: 18px;
}

/* ── Info row (location + cancellation) ── */
.info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding-top: 2px;
}
.infoCell {
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 8px;
}
.infoCell svg {
  width: 16px;
  height: 16px;
  color: var(--accent);
  stroke-width: 1.7;
  margin-top: 1px;
}
.infoT1 {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-primary);
}
.infoT2 {
  font-size: 11.5px;
  color: var(--text-secondary);
  line-height: 1.35;
}
.divider {
  height: 1px;
  background: var(--border-subtle);
}

/* ── SERVICE — date picker + time slots ── */
.bk {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.bkHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.bkHead h3 {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 19px;
  letter-spacing: -0.005em;
  color: var(--text-primary);
}
.bkTz {
  font-size: 11px;
  color: var(--text-tertiary);
}
/* ── Date strip: the shared `components/DateStrip` (its geometry IS this page's
   former `.dateStrip`/`.dateChip` block, moved there verbatim when the admin
   composer stopped keeping a copy — RULED 2026-08-19). What stays here is this
   world's palette mapped onto the component's own `--strip-*` vars, plus the
   page bleed that lets the strip scroll edge to edge inside the 20px gutter. ── */
.dateStripTheme {
  --strip-spine-bg: var(--bg-page);
  --strip-accent: var(--accent);
  --strip-accent-rgb: var(--accent-rgb);
  --strip-spine-font: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  --strip-chip-bg: var(--glass-bg);
  --strip-chip-ring: var(--border-subtle);
  --strip-dow-color: var(--text-tertiary);
  --strip-dnum-color: var(--text-primary);
  --strip-selected-dow-color: var(--event-date-color);
  /* An unavailable day reads as struck out here — this world's "disabled" is
     "cannot be booked". */
  --strip-disabled-num-decoration: line-through;
  margin: 0 -20px;
}
.timeGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.timeGroup .glabel {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-primary);
}
/* D: one horizontal scroll row per period — overflow scrolls sideways instead of
   wrapping into stacked rows that shove the page. Same pattern as the shared
   DateStrip / .galleryTrack; full-bleed so pills scroll edge-to-edge (the .glabel above stays
   at the normal inset). */
.timePills {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  margin: 0 -20px;
  padding: 2px 20px;
}
.timePills::-webkit-scrollbar {
  display: none;
}
/* C: while a newly-picked date's slots load, the previous pills stay mounted but
   dimmed + tap-blocked (also `disabled` in markup) so the grid keeps its height. */
.timePills.updating {
  opacity: 0.45;
  pointer-events: none;
}
.timePill {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  flex: 0 0 auto;
  padding: 9px 15px;
  border-radius: 9999px;
  background: var(--glass-bg);
  box-shadow: inset 0 0 0 1px var(--border-subtle);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  letter-spacing: -0.005em;
}
.timePill.selected {
  background: rgba(var(--accent-rgb), 0.18);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.6), 0 0 16px rgba(var(--accent-rgb), 0.16);
  color: var(--event-date-color);
}
/* Empty-period placeholder — keeps Morning/Afternoon/Evening rows always present at
   a stable height (matches a pill row) so switching dates never collapses the page. */
.timeEmpty {
  display: flex;
  align-items: center;
  min-height: 38px;
  font-size: 13px;
  color: var(--text-tertiary);
  letter-spacing: -0.005em;
}
.pickerState {
  font-size: 13px;
  color: var(--text-tertiary);
  padding: 6px 0;
}

/* ── EVENT — airy "When" + capacity ── */
.ev {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.evWhen {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.012em;
  color: var(--text-primary);
  line-height: 1.05;
}
.evMeta {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.evMeta .m {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: var(--text-secondary);
}
.evMeta .m svg {
  width: 13px;
  height: 13px;
  color: var(--accent);
  stroke-width: 1.8;
}
.evMeta .sep {
  width: 1px;
  height: 12px;
  background: var(--border-soft);
}
.evMeta .pr {
  margin-left: auto;
  font-size: 16px;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: -0.01em;
}
.cap {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding-top: 4px;
}
.capRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.capLabel {
  font-size: 11px;
  color: var(--text-tertiary);
}
.capbar {
  height: 6px;
  border-radius: 9999px;
  background: rgba(var(--fg-page-rgb), 0.08);
  overflow: hidden;
}
.capbar > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--capacity-fill-bg);
}
.spots {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--spots-low-color);
  white-space: nowrap;
}

/* ── Sticky bottom action bar ── */
.bottombar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 70;
  padding: 12px 16px calc(12px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 14px;
  background: linear-gradient(
    180deg,
    rgba(var(--bg-page-rgb), 0) 0%,
    rgba(var(--bg-page-rgb), 0.86) 26%,
    var(--bg-page) 100%
  );
}
.priceBlock .p1 {
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}
/* Payment-terms line (mockup C3): 11px accent copy + a 12px accent icon. */
.terms {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: var(--accent-hot);
}
.payIco {
  display: inline-flex;
  flex-shrink: 0;
}
.payIco svg {
  width: 12px;
  height: 12px;
  color: var(--accent);
  stroke-width: 2;
}
.bookBtn {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  /* Fixed height so the 1-line disabled state ("Select a time" / "Sold out") is
     the SAME size as the 2-line lit state (label + sub-line), and identical on the
     service AND event detail pages (shared button). (CWR 2.4.1-F) */
  min-height: 48px;
  padding: 9px 18px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.18;
  box-shadow: var(--glow-amber);
}
.bookBtn small {
  display: block;
  font-size: 10px;
  font-weight: 500;
  opacity: 0.85;
  letter-spacing: 0;
}
.bookBtn:disabled {
  background: var(--glass-bg-strong);
  color: var(--text-tertiary);
  box-shadow: none;
}
.bookError {
  grid-column: 1 / -1;
  margin-top: 6px;
  font-size: 12px;
  color: var(--accent);
  text-align: center;
}

/* ── Loading / not-found states ── */
.stateWrap {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
}
.stateTitle {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}
.stateBtn {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  margin-top: 8px;
  padding: 11px 22px;
  border-radius: 12px;
  border: 1px solid rgba(var(--accent-rgb), 0.55);
  color: var(--accent);
  font-size: 14px;
  font-weight: 600;
}
.skeletonHero {
  height: 330px;
  background: var(--glass-bg);
  opacity: 0.5;
}
.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid var(--border-soft);
  border-top-color: var(--accent);
  animation: sd-spin 0.8s linear infinite;
}
@keyframes sd-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Fullscreen photo lightbox (Gabriel-requested; not in the mockup) ──
   Intentionally theme-NEUTRAL: a photo viewer is universally black with white
   chrome + letterbox bars, so it uses literal #000/#fff rather than the
   per-tenant tokens (theming the bars would be wrong). */
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  animation: lb-in 0.3s cubic-bezier(0.2, 0.9, 0.25, 1);
}
@keyframes lb-in {
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
/* Black letterbox layer, separated from the container so the drag-to-dismiss
   gesture can fade it toward the page underneath (translucency). */
.lbBackdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: #000;
  transition: opacity 0.3s ease;
}
/* Holds the photo track + owns the vertical drag-to-dismiss transform (shrink),
   and all touch (`touch-action: none` → the gesture is fully ours). */
.lbViewport {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
  touch-action: none;
  /* Clip to the centered slide: the off-screen neighbours stay hidden even when
     the viewport scales down during the drag-to-dismiss (otherwise the shrunk
     side photos peek in from the edges). */
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.2, 0.9, 0.25, 1);
}
.lbTrack {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.32s cubic-bezier(0.2, 0.9, 0.25, 1);
}
.lbSlide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  /* Gutter between photos so adjacent images don't touch as you swipe (the slide
     stays one viewport wide via border-box, so the translateX step is exact). */
  box-sizing: border-box;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lbImg {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
}
.lbArrow {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
}
.lbArrow svg {
  width: 22px;
  height: 22px;
  stroke-width: 2;
}
.lbPrev {
  left: 12px;
}
.lbNext {
  right: 12px;
}
.lbCounter {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 20px);
  z-index: 2;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.6);
  transition: opacity 0.3s ease;
}
